import {
  Alert,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Paper,
  Typography,
  Skeleton,
  Box,
} from "@mui/material";
import DailyLandingPageExperience from "./DailyLandingPageExperience";
import { Grid, Stack } from "@mui/system";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useSelector } from "react-redux";
import { useFetchKeywordDetails } from "../../../../../../pages/PaidTrafficManagement/hooks/useFetchKeywordDetails";
import { formatToHumanReadable } from "../../../../../../utils/helpers";

const BestBettingSitesInfo = () => {
  const { keywordReleventScore, keywordReleventScoreLoading } = useSelector(
    (state: any) => state.landingPageExperience
  );
  const { postKeywordReleventScore } = useFetchKeywordDetails();

  const hasRelevanceScore =
    keywordReleventScore?.relevance_score !== undefined &&
    keywordReleventScore?.relevance_score !== null;
  const hasPositiveAspects = keywordReleventScore?.positive_aspects?.length > 0;
  const hasImprovementSuggestions =
    keywordReleventScore?.improvement_suggestions?.length > 0;

  const score = keywordReleventScore?.relevance_score;

  let color = "#D32F2F"; // default red
  if (score >= 8) {
    color = "#2E7D32"; // green
  } else if (score >= 5) {
    color = "#ED6C02"; // yellow/orange
  }

  return (
    <>
      <Paper sx={{ padding: "16px 16px 0px", mb: 2 }}>
        <Grid container mt={0} columnSpacing={4} mb={2} alignItems="stretch">
          <Grid size={2} display="flex">
            <Paper
              sx={{
                padding: "16px",
                minHeight: "255px",
                flex: 1,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Typography fontSize={18}>Relevance Score:</Typography>

              {/* Centered Score Section */}
              <Stack
                justifyContent="center"
                flexDirection="row"
                textAlign="center"
                py={6}
              >
                {keywordReleventScoreLoading ? (
                  <Skeleton
                    variant="rectangular"
                    width={80}
                    height={48}
                    sx={{ borderRadius: 1 }}
                  />
                ) : hasRelevanceScore ? (
                  <Alert
                    icon={false}
                    sx={{
                      p: "0px",
                      width: "80px",
                      textAlign: "center",
                      justifyContent: "center",
                      border: `1px solid ${color}80`,
                      backgroundColor: `${color}14`,
                      color,
                    }}
                  >
                    <Typography variant="body1" fontSize="24px">
                      {score}/10
                    </Typography>
                  </Alert>
                ) : (
                  <Box sx={{ textAlign: "center", py: 2 }}>
                    <Typography color="textSecondary">No data</Typography>
                  </Box>
                )}
              </Stack>

              {/* Last Updated Section (stuck at bottom) */}
              <Stack flexDirection="row" alignItems="center" mt="auto">
                <IconButton
                  aria-label="fingerprint"
                  color="secondary"
                  onClick={() => postKeywordReleventScore()}
                  disabled={keywordReleventScoreLoading}
                >
                  <RefreshIcon />
                </IconButton>

                {keywordReleventScoreLoading ? (
                  <Skeleton width={120} height={40} />
                ) : keywordReleventScore?.created ? (
                  <Typography
                    style={{ fontSize: "12px", lineHeight: "21px" }}
                    ml={1}
                  >
                    <span
                      style={{
                        fontSize: "12px",
                        display: "block",
                        fontWeight: "500",
                      }}
                    >
                      Last updated:
                    </span>
                    {formatToHumanReadable(keywordReleventScore?.created)}
                  </Typography>
                ) : (
                  <Typography
                    style={{ fontSize: "12px", lineHeight: "21px" }}
                    ml={1}
                    color="textSecondary"
                  >
                    No update data
                  </Typography>
                )}
              </Stack>
            </Paper>
          </Grid>

          <Grid size={5} display="flex">
            <Paper
              sx={{
                padding: "16px",
                minHeight: "255px",
                flex: 1,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Typography fontSize={18}>What's Working:</Typography>

              <List>
                {keywordReleventScoreLoading ? (
                  Array.from(new Array(3)).map((_, index) => (
                    <ListItem key={index}>
                      <ListItemAvatar>
                        <Skeleton variant="circular" width={28} height={28} />
                      </ListItemAvatar>
                      <ListItemText
                        sx={{ mt: "0px" }}
                        secondary={
                          <Skeleton variant="text" width="80%" height={20} />
                        }
                      />
                    </ListItem>
                  ))
                ) : hasPositiveAspects ? (
                  keywordReleventScore?.positive_aspects?.map(
                    (aspect: any, index: number) => (
                      <ListItem key={index}>
                        <ListItemAvatar>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="28"
                            height="28"
                            fill="none"
                          >
                            <g clipPath="url(#a)">
                              <rect
                                width="28"
                                height="28"
                                fill="#EDF7ED"
                                rx="14"
                              />
                              <g clipPath="url(#b)">
                                <path
                                  fill="#1E4620"
                                  d="m11.33 17.23-3.476-3.476-1.183 1.175 4.658 4.658 10-10-1.175-1.174-8.825 8.816Z"
                                />
                              </g>
                            </g>
                            <rect
                              width="27"
                              height="27"
                              x=".5"
                              y=".5"
                              stroke="#2E7D32"
                              strokeOpacity=".5"
                              rx="13.5"
                            />
                            <defs>
                              <clipPath id="a">
                                <rect
                                  width="28"
                                  height="28"
                                  fill="#fff"
                                  rx="14"
                                />
                              </clipPath>
                              <clipPath id="b">
                                <path fill="#fff" d="M0 0h28v28H0z" />
                              </clipPath>
                            </defs>
                          </svg>
                        </ListItemAvatar>
                        <ListItemText sx={{ mt: "0px" }} secondary={aspect} />
                      </ListItem>
                    )
                  )
                ) : (
                  <Box sx={{ textAlign: "center", py: 4 }}>
                    <Typography color="textSecondary">
                      No data available
                    </Typography>
                  </Box>
                )}
              </List>
            </Paper>
          </Grid>
          <Grid size={5} display="flex">
            <Paper
              sx={{
                padding: "16px",
                minHeight: "255px",
                flex: 1,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Typography fontSize={18}>Optimization Needed:</Typography>

              <List>
                {keywordReleventScoreLoading ? (
                  Array.from(new Array(3)).map((_, index) => (
                    <ListItem key={index} sx={index > 0 ? { my: "5px" } : {}}>
                      <ListItemAvatar>
                        <Skeleton variant="circular" width={28} height={28} />
                      </ListItemAvatar>
                      <ListItemText
                        sx={{ mt: "0px" }}
                        secondary={
                          <Skeleton variant="text" width="80%" height={20} />
                        }
                      />
                    </ListItem>
                  ))
                ) : hasImprovementSuggestions ? (
                  keywordReleventScore?.improvement_suggestions?.map(
                    (suggestion: any, index: number) => (
                      <ListItem key={index} sx={index > 0 ? { my: "5px" } : {}}>
                        <ListItemAvatar>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="28"
                            height="28"
                            fill="none"
                          >
                            <g clipPath="url(#a)">
                              <rect
                                width="28"
                                height="28"
                                fill="#FDEDED"
                                rx="14"
                              />
                              <g clipPath="url(#b)">
                                <path
                                  fill="#5F2120"
                                  d="m19.833 9.342-1.175-1.175L14 12.825 9.342 8.167 8.167 9.342 12.825 14l-4.658 4.658 1.175 1.175L14 15.175l4.658 4.658 1.175-1.175L15.175 14l4.658-4.658Z"
                                />
                              </g>
                            </g>
                            <rect
                              width="27"
                              height="27"
                              x=".5"
                              y=".5"
                              stroke="#D32F2F"
                              strokeOpacity=".5"
                              rx="13.5"
                            />
                            <defs>
                              <clipPath id="a">
                                <rect
                                  width="28"
                                  height="28"
                                  fill="#fff"
                                  rx="14"
                                />
                              </clipPath>
                              <clipPath id="b">
                                <path fill="#fff" d="M0 0h28v28H0z" />
                              </clipPath>
                            </defs>
                          </svg>
                        </ListItemAvatar>
                        <ListItemText
                          sx={{ mt: "0px" }}
                          secondary={suggestion}
                        />
                      </ListItem>
                    )
                  )
                ) : (
                  <Box sx={{ textAlign: "center", py: 4 }}>
                    <Typography color="textSecondary">
                      No data available
                    </Typography>
                  </Box>
                )}
              </List>
            </Paper>
          </Grid>
        </Grid>

        <DailyLandingPageExperience />
      </Paper>
    </>
  );
};

export default BestBettingSitesInfo;
